import type { ReactNode } from "react";

/** Tinta do relatório — a mesma dos gráficos (ver charts/options.ts). */
export const INK = {
  brand: "#0C2856",
  primary: "#0b0b0b",
  secondary: "#52514e",
  rule: "#e5e4e0",
  surfaceAlt: "#f6f6f4",
} as const;

export const nf = (value: number | null | undefined, digits = 1): string =>
  value == null || Number.isNaN(value)
    ? "—"
    : value.toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      });

export const pct = (value: number | null | undefined, digits = 1): string =>
  value == null ? "—" : `${nf(value, digits)}%`;

/** Título de seção numerada; cada seção começa em página nova no PDF. */
export function SectionTitle({
  number,
  children,
  pageBreak = true,
}: {
  number?: string;
  children: ReactNode;
  pageBreak?: boolean;
}) {
  return (
    <h2
      className={`${pageBreak ? "page-break " : ""}text-[1.15rem] font-bold mt-8 mb-3 pb-1.5 border-b-2`}
      style={{ color: INK.brand, borderColor: INK.brand }}
    >
      {number && <span className="mr-2">{number}</span>}
      {children}
    </h2>
  );
}

export function SubTitle({ children }: { children: ReactNode }) {
  return (
    <h3
      className="text-[0.95rem] font-semibold mt-5 mb-2 break-after-avoid"
      style={{ color: INK.brand }}
    >
      {children}
    </h3>
  );
}

export function Lead({ children }: { children: ReactNode }) {
  return (
    <p className="text-[0.82rem] leading-relaxed mb-3" style={{ color: INK.secondary }}>
      {children}
    </p>
  );
}

export function Unavailable({ what }: { what: string }) {
  return (
    <p
      className="text-[0.8rem] italic rounded px-3 py-2 my-2"
      style={{ color: INK.secondary, background: INK.surfaceAlt }}
    >
      {what}: dado indisponível no momento da geração do relatório.
    </p>
  );
}

export interface Column<T> {
  header: string;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
  /** Largura relativa, em CSS. */
  width?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  emphasizeRow,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  caption?: string;
  emphasizeRow?: (row: T) => boolean;
}) {
  return (
    <table className="w-full border-collapse text-[0.78rem] my-2">
      {caption && (
        <caption
          className="text-left text-[0.75rem] mb-1"
          style={{ color: INK.secondary, captionSide: "top" }}
        >
          {caption}
        </caption>
      )}
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.header}
              className={`py-1.5 px-2 font-semibold border-b-2 ${c.align === "right" ? "text-right" : "text-left"}`}
              style={{ color: INK.brand, borderColor: INK.brand, width: c.width }}
            >
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={rowKey(row, i)}
            style={{
              background: emphasizeRow?.(row) ? INK.surfaceAlt : undefined,
              fontWeight: emphasizeRow?.(row) ? 600 : undefined,
            }}
          >
            {columns.map((c) => (
              <td
                key={c.header}
                className={`py-1.5 px-2 border-b align-top ${c.align === "right" ? "text-right tabular-nums" : "text-left"}`}
                style={{ color: INK.primary, borderColor: INK.rule }}
              >
                {c.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function KeyNumber({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div
      className="rounded-md border px-3 py-2.5 flex flex-col gap-1"
      style={{ borderColor: INK.rule }}
    >
      <span className="text-[0.7rem] leading-snug" style={{ color: INK.secondary }}>
        {label}
      </span>
      <span className="text-[1rem] font-bold leading-tight" style={{ color: INK.primary }}>
        {value}
      </span>
      {detail && (
        <span className="text-[0.68rem]" style={{ color: INK.secondary }}>
          {detail}
        </span>
      )}
    </div>
  );
}

export function Figure({
  caption,
  children,
}: {
  caption: string;
  children: ReactNode;
}) {
  return (
    <figure className="chart-item my-3">
      {children}
      <figcaption className="text-[0.72rem] mt-1" style={{ color: INK.secondary }}>
        {caption}
      </figcaption>
    </figure>
  );
}
